<template>
  <el-dialog
    v-model="visible"
    :width="$isZh() ? '440px' : '500px'"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :before-close="beforeClose"
    class="phone-verify-dialog"
  >
    <template #header>
      <div class="pv-title">{{ $t('account_manager.phone_verify.title') }}</div>
    </template>
    <div class="pv-notice">
      <svg-icon name="info"></svg-icon>
      <span>{{ $t('account_manager.phone_verify.notice') }}</span>
    </div>
    <div class="pv-body">
      <!-- 情况一：有手机号，默认状态 -->
      <div class="pv-row" v-if="hasPhone && mode === 'default'">
        <span class="pv-text">{{ $t('account_manager.phone_verify.used_phone', ['+86 ' + maskedPhone]) }}</span>
        <span class="pv-link" @click="toChangeMode">{{ $t('account_manager.phone_verify.change_phone') }}</span>
      </div>
      <!-- 情况一：有手机号，更换状态 -->
      <template v-else-if="hasPhone && mode === 'change'">
        <div class="pv-label-row">
          <span class="pv-label">{{ $t('account_manager.phone_verify.new_phone') }}</span>
          <span class="pv-link" @click="toDefaultMode">{{ $t('account_manager.phone_verify.use_original_phone') }}</span>
        </div>
        <el-input
          class="pv-phone-input"
          v-model.trim="newPhone"
          :placeholder="$t('account_manager.phone_verify.input_new_phone')"
          onkeypress="return(/[\d]/.test(String.fromCharCode(event.keyCode)))"
          maxlength="11"
        >
          <template #prefix>
            <span class="pv-prefix">+86</span>
            <span class="pv-divider"></span>
          </template>
        </el-input>
      </template>
      <!-- 情况二：无手机号 -->
      <template v-else>
        <div class="pv-label-row">
          <span class="pv-label">{{ $t('account_manager.phone_verify.phone') }}</span>
        </div>
        <el-input
          class="pv-phone-input"
          v-model.trim="newPhone"
          :placeholder="$t('account_manager.phone_verify.input_phone')"
          onkeypress="return(/[\d]/.test(String.fromCharCode(event.keyCode)))"
          maxlength="11"
        >
          <template #prefix>
            <span class="pv-prefix">+86</span>
            <span class="pv-divider"></span>
          </template>
        </el-input>
      </template>
      <div class="pv-row pv-code-row">
        <el-input
          class="pv-code-input"
          type="text"
          maxlength="6"
          :placeholder="$t('account_manager.phone_verify.input_code')"
          v-model="code"
        />
        <verificationCodeBtn ref="verificationRef" @send="handleSend" :loading="loading" />
      </div>
      <div class="pv-error" v-if="errorText">{{ errorText }}</div>
    </div>
    <template #footer>
      <el-button link @click="handleLogout">{{ $t('account_manager.phone_verify.logout') }}</el-button>
      <el-button type="primary" :disabled="!canConfirm" @click="handleConfirm">{{ $t('account_manager.phone_verify.confirm') }}</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import verificationCodeBtn from '@/views/user-center/account/components/verification-code-btn.vue'
import { sendPhoneVerifyCode, phoneVerify } from '@/services/account'
import { message } from '@/hooks/useUI'
import { isPhone } from '@/utils/validate'
import { t } from 'app/i18n'

const store = useStore()
const visible = ref(true)
const mode = ref('default') // default | change
const newPhone = ref('')
const code = ref('')
const loading = ref(false)
const errorText = ref('')
const verificationRef = ref()

const hasPhone = computed(() => !!store.getters.userInfo.phone)
const maskedPhone = computed(() => (store.getters.userInfo.phone || '').replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2'))
const isChangeInput = computed(() => !hasPhone.value || mode.value === 'change')
const targetPhone = computed(() => (isChangeInput.value ? newPhone.value : store.getters.userInfo.phone))
const maxLength = 6

const canConfirm = computed(() => {
  if (!code.value || code.value.length < maxLength) return false
  if (isChangeInput.value) return isPhone(newPhone.value)
  return true
})

const beforeClose = () => {}

const toChangeMode = () => {
  mode.value = 'change'
  invalidateCode()
}
const toDefaultMode = () => {
  mode.value = 'default'
  invalidateCode()
}
const invalidateCode = () => {
  code.value = ''
  errorText.value = ''
  newPhone.value = ''
  verificationRef.value && verificationRef.value.reset()
}

const handleSend = () => {
  if (isChangeInput.value && !isPhone(newPhone.value)) {
    message(t('account_manager.edit_user.phone_valid'), 'error')
    return
  }
  loading.value = true
  sendPhoneVerifyCode(isChangeInput.value ? { phone: newPhone.value } : {}).then(res => {
    if (res.code && res.code !== 0) {
      if (res.code === 1003) {
        message(t('account_manager.phone_verify.err_code'), 'error')
      } else if (res.code === 4101) {
        message(t('account_manager.phone_verify.phone_occupied'), 'error')
      } else if (res.code === 4110) {
        message(t('account_manager.phone_verify.phone_total_limited'), 'error')
      } else if (res.code === 4111) {
        message(t('account_manager.phone_verify.phone_frequency_limited'), 'error')
      } else {
        message(t('account_manager.phone_verify.send_err'), 'error')
      }
    } else {
      message(t('account_manager.edit_user.code_sended'), 'success')
      verificationRef.value.tackBtn()
    }
  }).finally(() => { loading.value = false })
}

const handleConfirm = () => {
  if (!canConfirm.value) return
  errorText.value = ''
  phoneVerify({ phone: targetPhone.value, code: code.value }).then(res => {
    if (res.code && res.code !== 0) {
      if (res.code === 1003) {
        errorText.value = t('account_manager.phone_verify.err_code')
      } else if (res.code === 4101) {
        errorText.value = t('account_manager.phone_verify.phone_occupied')
      } else {
        message(res.message, 'error')
      }
    } else {
      const userInfo = store.getters.userInfo
      store.commit('user/setUserInfo', { ...userInfo, phone: targetPhone.value, phone_verified: true })
      message(t('account_manager.phone_verify.success'), 'success')
      visible.value = false
    }
  })
}

const handleLogout = async () => {
  await store.dispatch('user/logout')
}
</script>

<style lang="scss" scoped>
@import '@/styles/common.scss';
.phone-verify-dialog {
  .pv-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-1);
  }
  .pv-notice {
    display: flex;
    align-items: flex-start;
    background-color: var(--color-warning-tip);
    color: var(--color-text-1);
    font-size: 13px;
    line-height: 20px;
    padding: 10px 16px;
    border-radius: 2px;
    svg {
      color: var(--color-warning-text);
      font-size: 16px;
      margin-right: 6px;
      flex-shrink: 0;
      margin-top: 2px;
    }
  }
  .pv-body {
    padding-top: 20px;
    .pv-row {
      display: flex;
      align-items: center;
      min-height: 32px;
      margin-bottom: 16px;
      &.pv-code-row {
        margin-bottom: 8px;
      }
      .pv-text {
        flex: 1;
        font-size: 14px;
        color: var(--color-text-1);
        margin-right: 12px;
      }
      .pv-label {
        font-size: 14px;
        color: var(--color-text-1);
        flex-shrink: 0;
        margin-right: 12px;
      }
      .pv-link {
        font-size: 14px;
        color: var(--color-primary);
        cursor: pointer;
        flex-shrink: 0;
        white-space: nowrap;
      }
      .pv-code-input {
        flex: 1;
      }
    }
    .pv-label-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
      .pv-label {
        font-size: 14px;
        color: var(--color-text-1);
      }
      .pv-link {
        font-size: 14px;
        color: var(--color-primary);
        cursor: pointer;
        white-space: nowrap;
      }
    }
    .pv-phone-input {
      width: 100%;
      margin-bottom: 16px;
      .pv-prefix {
        color: var(--color-text-1);
      }
      .pv-divider {
        width: 1px;
        align-self: stretch;
        margin: 0 8px;
        background: var(--color-border-2);
      }
    }
    .pv-error {
      font-size: 12px;
      color: var(--color-danger-text);
      line-height: 18px;
      padding-left: 2px;
    }
  }
}
</style>

<style lang="scss">
.phone-verify-dialog .el-dialog__header {
  border-bottom: none;
  padding-top: 20px;
}
</style>
